<script setup lang="ts">
import {
  UserCircleIcon,
  DocumentDownloadIcon,
  HeartIcon,
  ShoppingCartIcon,
  UserIcon,
  UsersIcon,
  CurrencyPoundIcon,
  ClockIcon,
  ClipboardListIcon,
  DocumentReportIcon,
  LogoutIcon,
  CollectionIcon
} from '@heroicons/vue/solid';
import { ChevronRightIcon, ArrowLeftIcon } from '@heroicons/vue/outline';
import DynamicIcon from '~/components/ui/icons/DynamicIcon.vue';

const showMyAccountSubMenu = ref(false);
const showTradeResourcesSubMenu = ref(false);

const { userData, logout } = useAuth();

const isAdmin = computed(() => userData.value?.dealers?.some(dealer => dealer.role === 'Admin'));

const siteSettingsData = inject('siteSettings');

const tradeResources = computed(() => {
  return siteSettingsData?.tradeResources || [];
});

const {
  public: { favouritesEnabled }
} = useRuntimeConfig();
</script>

<template>
  <ul class="flex flex-col pb-[env(safe-area-inset-bottom)] font-medium text-stormcloud">
    <li
      class="flex flex-row items-center justify-start gap-2 border-b-1 border-gainsboro bg-anti-flash-white px-5 py-4"
      @click="showMyAccountSubMenu = true"
    >
      <UserCircleIcon class="size-4 stroke-2" />
      My Account
      <ChevronRightIcon class="ml-auto size-4 stroke-3" />
      <div
        class="absolute top-0 z-40 h-svh w-full bg-white"
        :class="[
          'left-0 top-0 transform transition-transform',
          showMyAccountSubMenu ? 'translate-x-0' : '-translate-x-full'
        ]"
      >
        <div
          class="flex h-[60px] w-full items-center bg-gunmetal px-5 font-heading uppercase tracking-heading text-white"
        >
          <button class="flex flex-row items-center gap-3 uppercase" @click.stop="showMyAccountSubMenu = false">
            <ArrowLeftIcon class="size-4 stroke-2" />
            My Account
          </button>
        </div>
        <ul
          class="flex h-full flex-col overflow-y-scroll bg-white pb-[env(safe-area-inset-bottom)] font-medium capitalize"
        >
          <li class="flex flex-row items-center justify-between bg-anti-flash-white px-5 py-4">
            Hello {{ userData?.humanName.firstName }}
          </li>
          <nuxt-link
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            to="/account"
          >
            <UserIcon class="size-4" /> My details
          </nuxt-link>
          <nuxt-link
            v-if="isAdmin"
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            to="/account/manage-users"
          >
            <UsersIcon class="size-4" /> Manage users
          </nuxt-link>
          <nuxt-link
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            to="/account/order-history"
          >
            <ClockIcon class="size-4" /> Order history
          </nuxt-link>
          <nuxt-link
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            to="/account/unsubmitted"
          >
            <CurrencyPoundIcon class="size-4" /> Unsubmitted orders
          </nuxt-link>
          <nuxt-link
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            to="/account/invoices"
          >
            <ClipboardListIcon class="size-4" /> Invoices
          </nuxt-link>
          <nuxt-link
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            to="/account/statements"
          >
            <DocumentReportIcon class="size-4" /> Statements
          </nuxt-link>
          <button
            variant="plain"
            class="flex flex-row items-center gap-2 border-b-0 border-gainsboro px-5 py-4 font-normal"
            @click="() => logout()"
          >
            <LogoutIcon class="size-4" /> Logout
          </button>
        </ul>
      </div>
    </li>
    <li v-if="favouritesEnabled">
      <nuxt-link
        to="/favourites"
        class="flex flex-row items-center justify-start gap-2 border-b-1 border-gainsboro px-5 py-4"
      >
        <HeartIcon class="size-4 stroke-2" />
        Favourites
      </nuxt-link>
    </li>
    <li
      class="flex flex-row items-center justify-start gap-2 border-b-1 border-gainsboro px-5 py-4"
      @click="showTradeResourcesSubMenu = true"
    >
      <DocumentDownloadIcon class="size-4 stroke-2" />
      Trade Resources
      <ChevronRightIcon class="ml-auto size-4 stroke-3" />
      <div
        class="absolute top-0 z-40 h-svh w-full bg-white"
        :class="[
          'left-0 top-0 transform transition-transform',
          showTradeResourcesSubMenu ? 'translate-x-0' : '-translate-x-full'
        ]"
      >
        <div
          class="flex h-[60px] w-full items-center bg-gunmetal px-5 font-heading uppercase tracking-heading text-white"
        >
          <button class="flex flex-row items-center gap-3 uppercase" @click.stop="showTradeResourcesSubMenu = false">
            <ArrowLeftIcon class="size-4 stroke-2" />
            My Account
          </button>
        </div>
        <ul
          class="flex h-full flex-col overflow-y-scroll bg-white pb-[env(safe-area-inset-bottom)] font-medium capitalize"
        >
          <li class="flex flex-row items-center justify-between bg-anti-flash-white px-5 py-4">
            Hello {{ userData?.humanName.firstName }}
          </li>
          <nuxt-link
            v-for="(item, index) in tradeResources"
            :key="`trade-resource-link-${index}`"
            class="flex flex-row items-center gap-2 border-b-1 border-gainsboro px-5 py-4 font-normal"
            :href="item?.link.href"
          >
            <DynamicIcon v-if="item.iconAlias" :name="item.iconAlias" class="size-4" aria-hidden="true" />
            <CollectionIcon v-else class="size-4" aria-hidden="true" />
            {{ item?.link?.title }}
          </nuxt-link>
        </ul>
      </div>
    </li>
    <li>
      <nuxt-link
        to="/basket"
        class="flex flex-row items-center justify-start gap-2 border-b-1 border-gainsboro px-5 py-4"
      >
        <ShoppingCartIcon class="size-4 stroke-2" />
        Basket
      </nuxt-link>
    </li>
  </ul>
</template>
